// src/pages/Platform/CompanyDetail.js
//
// One company's own detail screen — GetCompanyByIDHandler's {company, events}
// pair (internal/handlers/platform_company_handler.go), plus the renew/
// suspend/reactivate/retry-provisioning actions that same file's own doc
// comments describe. Same plain-markup approach as Companies.js (see that
// file's own doc comment on why components/UI/* isn't reused here).
import { useState, useEffect, useCallback, useContext } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  getCompanyById,
  renewCompany,
  suspendCompany,
  reactivateCompany,
  cancelCompany,
  deleteCompany,
  getCompanyDeletionStatus,
  retryCompanyProvisioning,
  forceActivateCompany,
  resetCompanyAdminPassword,
} from "../../services/api";
import { PlatformAuthContext } from "../../context/PlatformAuthContext";

// DELETE_STEPS mirrors the real stages DeleteCompanyHandler's own
// background goroutine reports through updateDeletionProgress
// (internal/handlers/platform_company_handler.go) — same "show the actual
// stages, not a fake spinner" request that motivated Companies.js's own
// ProvisioningProgress/provisioningSteps for the Add Company flow. "done"
// itself isn't listed as a row here — it's the terminal state that ends
// the progress view entirely (navigate away on success) rather than one
// more line to light up.
const DELETE_STEPS = [
  { key: "closing_connections", label: "Closing active connections" },
  { key: "dropping_database", label: "Dropping the company's database" },
  { key: "removing_files", label: "Removing uploaded files" },
  { key: "deleting_records", label: "Deleting company records" },
];

// deletionStepState places one DELETE_STEPS row into the same done/active/
// failed/waiting vocabulary Companies.js's own StepIcon already renders —
// duplicated here (not imported) since Companies.js doesn't export either
// helper and the two files' own doc comments already treat this plain-
// markup-per-screen approach as this Platform section's normal style.
function deletionStepState(stepKey, currentStage, hasError) {
  const order = DELETE_STEPS.map((s) => s.key);
  const currentIndex = order.indexOf(currentStage);
  const stepIndex = order.indexOf(stepKey);
  if (hasError && stepIndex === currentIndex) return "failed";
  if (stepIndex < currentIndex || currentStage === "done") return "done";
  if (stepIndex === currentIndex) return "active";
  return "waiting";
}

function StepIcon({ state }) {
  const glyph = { done: "✓", active: "●", failed: "✗", waiting: "○" }[state];
  const color = { done: "#16a34a", active: "#2563eb", failed: "#dc2626", waiting: "#cbd5e1" }[state];
  return <span style={{ color, fontWeight: 700, width: 16, display: "inline-block" }}>{glyph}</span>;
}

const EVENT_LABELS = {
  created: "Created",
  renewed: "Renewed",
  reminder_sent: "Reminder sent",
  grace_started: "Grace period started",
  suspended: "Suspended",
  reactivated: "Reactivated",
  cancelled: "Cancelled",
};

export default function CompanyDetail() {
  const { id } = useParams();
  const { token, permissions } = useContext(PlatformAuthContext);
  const navigate = useNavigate();

  const [company, setCompany] = useState(null);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [polling, setPolling] = useState(false);
  const [renewDays, setRenewDays] = useState(30);
  const [showRenew, setShowRenew] = useState(false);
  const [showSuspend, setShowSuspend] = useState(false);
  const [suspendReason, setSuspendReason] = useState("");
  const [showCancel, setShowCancel] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [showDelete, setShowDelete] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [deleteStage, setDeleteStage] = useState(null);
  const [deleteError, setDeleteError] = useState(null);
  const [deleteTimedOut, setDeleteTimedOut] = useState(false);
  const [resetResult, setResetResult] = useState(null);
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [resetEmailInput, setResetEmailInput] = useState("");

  const has = (p) => permissions.includes(p);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getCompanyById(token, id);
      setCompany(res.company);
      setEvents(res.events || []);
    } catch (err) {
      console.error("Failed to load company:", err.message);
    } finally {
      setLoading(false);
    }
  }, [token, id]);

  useEffect(() => {
    load();
  }, [load]);

  const runAction = async (fn) => {
    setBusy(true);
    try {
      await fn();
      await load();
    } catch (err) {
      alert(err.message || "Action failed");
    } finally {
      setBusy(false);
    }
  };

  // RetryProvisioningHandler kicks off ProvisionCompany in a background
  // goroutine on the server and responds immediately with "Retry started"
  // (see that handler's own doc comment) — the actual database/TLS work
  // still takes real time to run. The ordinary runAction pattern above
  // (call, then reload the company ONCE) reads the OLD provisioning_status
  // back almost every time, which is exactly why clicking this looked like
  // nothing happened. This confirms the click landed with an immediate
  // toast, then polls provisioning_status every 2s for up to ~16s so the
  // screen visibly updates once the retry actually finishes (or fails
  // again) instead of requiring a manual page refresh.
  const runRetryProvisioning = async () => {
    const statusBefore = company.provisioning_status;
    setBusy(true);
    try {
      await retryCompanyProvisioning(token, id);
      toast.info("Retry started — checking status...");
      setPolling(true);
      for (let i = 0; i < 8; i++) {
        await new Promise((resolve) => setTimeout(resolve, 2000));
        const res = await getCompanyById(token, id);
        setCompany(res.company);
        setEvents(res.events || []);
        if (res.company.provisioning_status !== statusBefore) {
          if (res.company.provisioning_status === "ready") {
            toast.success("Provisioning finished — the company is ready.");
          } else if (res.company.provisioning_error) {
            toast.error(`Still not ready (${res.company.provisioning_status}): ${res.company.provisioning_error}`);
          } else {
            toast.info(`Provisioning status: ${res.company.provisioning_status}`);
          }
          break;
        }
        if (i === 7) {
          toast.info("Still working — refresh in a moment to see the latest status.");
        }
      }
    } catch (err) {
      alert(err.message || "Retry failed");
    } finally {
      setPolling(false);
      setBusy(false);
    }
  };

  // ForceActivateCompanyHandler answers synchronously (unlike retry, there is
  // no background goroutine to poll for) — a toast plus the ordinary
  // reload-once from runAction is enough feedback here, matching the "make
  // something show that an action is happening" request that motivated
  // runRetryProvisioning's polling above.
  const runForceActivate = async () => {
    setBusy(true);
    try {
      await forceActivateCompany(token, id);
      toast.success("Company activated — it will now serve tenant traffic.");
      await load();
    } catch (err) {
      toast.error(err.message || "Force activate failed");
    } finally {
      setBusy(false);
    }
  };

  // ResetCompanyAdminPasswordHandler returns the new admin_email/
  // admin_password directly in its response (it doesn't email anything) — so
  // the only way the Super Admin can ever see this password is right here,
  // right now. Shown once in a dedicated modal rather than an alert() so it
  // can be selected/copied.
  //
  // Also takes an optional new email: the "wrong login" problem this button
  // exists for isn't only a wrong password — a failed first provisioning
  // attempt (fixed already, but earlier companies can still carry the
  // scars of it) or a blank retry both fall back to seeding
  // admin@example.com rather than whatever was typed into the Add Company
  // form, and there is no record anywhere of what that original value was.
  // Leaving the field blank here only resets the password, same as before.
  const runResetPassword = async (newEmail) => {
    setBusy(true);
    try {
      const res = await resetCompanyAdminPassword(token, id, newEmail);
      setResetResult(res);
      setShowResetPassword(false);
      setResetEmailInput("");
      toast.success(newEmail ? "Admin login updated." : "Admin password reset.");
    } catch (err) {
      toast.error(err.message || "Reset failed");
    } finally {
      setBusy(false);
    }
  };

  // DeleteCompanyHandler now only kicks the real deletion off in the
  // background (dropping a real Postgres database and removing an
  // uploads folder is genuinely slow enough to need this — same "show
  // the actual stages and update live" request that motivated
  // Companies.js's own ProvisioningProgress) and answers immediately, so
  // this switches the modal into the DELETE_STEPS progress view and polls
  // getCompanyDeletionStatus the same way runRetryProvisioning above
  // polls provisioning_status — except there's no Company row left to
  // read once it finishes successfully, so a "done" with no error means
  // navigate away, not reload.
  const runDelete = async () => {
    setBusy(true);
    setDeleteError(null);
    setDeleteTimedOut(false);
    try {
      await deleteCompany(token, id, deleteConfirmText.trim());
    } catch (err) {
      toast.error(err.message || "Delete failed");
      setBusy(false);
      return;
    }

    setDeleting(true);
    setDeleteStage("closing_connections");

    for (let i = 0; i < 40; i++) {
      await new Promise((resolve) => setTimeout(resolve, 1500));
      try {
        const res = await getCompanyDeletionStatus(token, id);
        setDeleteStage(res.stage);
        if (res.done) {
          if (res.error) {
            setDeleteError(res.error);
            toast.error(res.error);
            setBusy(false);
          } else {
            toast.success("Company deleted.");
            navigate("/platform/companies");
          }
          return;
        }
      } catch (err) {
        // A transient network hiccup shouldn't stop the progress view —
        // just try again on the next tick, same as runRetryProvisioning.
      }
      if (i === 39) {
        setDeleteTimedOut(true);
      }
    }
    setBusy(false);
  };

  if (loading) return <div style={{ padding: 40, textAlign: "center", color: "#94a3b8" }}>Loading...</div>;
  if (!company) return <div style={{ padding: 40, textAlign: "center", color: "#94a3b8" }}>Company not found.</div>;

  return (
    <div>
      <button style={backBtnStyle} onClick={() => navigate("/platform/companies")}>
        &larr; Back to companies
      </button>

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 22, color: "#0f172a" }}>{company.name}</h2>
          <div style={{ color: "#64748b", fontSize: 14, marginTop: 4 }}>
            {company.subdomain}.posflect.com
          </div>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          {has("company_retry_provisioning") && company.provisioning_status !== "ready" && (
            <button
              style={secondaryBtnStyle}
              disabled={busy}
              onClick={runRetryProvisioning}
            >
              {polling ? "Checking status…" : busy ? "Starting…" : "Retry provisioning"}
            </button>
          )}
          {has("company_renew") && (
            <button style={secondaryBtnStyle} disabled={busy} onClick={() => setShowRenew(true)}>
              Renew
            </button>
          )}
          {has("company_suspend") && company.status !== "suspended" && (
            <button style={dangerBtnStyle} disabled={busy} onClick={() => setShowSuspend(true)}>
              Suspend
            </button>
          )}
          {has("company_reactivate") && (company.status === "suspended" || company.status === "cancelled") && (
            <button
              style={primaryBtnStyle}
              disabled={busy}
              onClick={() => runAction(() => reactivateCompany(token, id))}
            >
              Reactivate
            </button>
          )}
          {has("company_cancel") && company.status !== "cancelled" && (
            <button style={dangerBtnStyle} disabled={busy} onClick={() => setShowCancel(true)}>
              Cancel subscription
            </button>
          )}
          {has("company_delete") &&
            (company.status === "provisioning" || company.status === "cancelled") && (
              <button style={dangerBtnStyle} disabled={busy} onClick={() => setShowDelete(true)}>
                Delete permanently
              </button>
            )}
          {has("company_force_activate") &&
            company.status !== "active" &&
            (company.provisioning_status === "db_ready" || company.provisioning_status === "tls_failed") && (
              <button style={dangerBtnStyle} disabled={busy} onClick={runForceActivate} title="Activates the company even though the automatic TLS/certificate step never succeeded.">
                Force activate
              </button>
            )}
          {has("company_reset_admin_password") && company.provisioning_status !== "pending" && (
            <button style={secondaryBtnStyle} disabled={busy} onClick={() => setShowResetPassword(true)}>
              Reset admin password
            </button>
          )}
        </div>
      </div>

      <div style={cardGridStyle}>
        <InfoCard label="Status" value={company.status} />
        <InfoCard label="Provisioning" value={company.provisioning_status} sub={company.provisioning_error} />
        <InfoCard label="Contact email" value={company.contact_email} />
        <InfoCard
          label="Subscription"
          value={company.expires_at ? `Expires ${new Date(company.expires_at).toLocaleDateString()}` : "—"}
          sub={company.subscription_started_at ? `Started ${new Date(company.subscription_started_at).toLocaleDateString()}` : ""}
        />
      </div>

      <h3 style={{ fontSize: 15, color: "#0f172a", marginTop: 28 }}>History</h3>
      <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", overflow: "hidden" }}>
        {events.length === 0 ? (
          <div style={{ padding: 24, textAlign: "center", color: "#94a3b8" }}>No events yet.</div>
        ) : (
          events.map((e) => (
            <div key={e.id} style={eventRowStyle}>
              <div style={{ fontWeight: 600, color: "#0f172a", fontSize: 13 }}>
                {EVENT_LABELS[e.event_type] || e.event_type}
              </div>
              <div style={{ fontSize: 13, color: "#64748b" }}>{e.detail}</div>
              <div style={{ fontSize: 12, color: "#94a3b8" }}>{new Date(e.created_at).toLocaleString()}</div>
            </div>
          ))
        )}
      </div>

      {showRenew && (
        <Modal onClose={() => setShowRenew(false)}>
          <h3 style={{ marginTop: 0 }}>Renew subscription</h3>
          <label style={labelStyle}>Extend by (days)</label>
          <input
            style={inputStyle}
            type="number"
            min="1"
            value={renewDays}
            onChange={(e) => setRenewDays(e.target.value)}
          />
          <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
            <button style={secondaryBtnStyle} onClick={() => setShowRenew(false)} disabled={busy}>Cancel</button>
            <button
              style={primaryBtnStyle}
              disabled={busy}
              onClick={() =>
                runAction(() => renewCompany(token, id, Number(renewDays) || 30)).then(() => setShowRenew(false))
              }
            >
              Confirm renewal
            </button>
          </div>
        </Modal>
      )}

      {showSuspend && (
        <Modal onClose={() => setShowSuspend(false)}>
          <h3 style={{ marginTop: 0 }}>Suspend company</h3>
          <p style={{ fontSize: 13, color: "#64748b" }}>
            This company's users will lose access immediately. Their data is not deleted.
          </p>
          <label style={labelStyle}>Reason (optional)</label>
          <input style={inputStyle} value={suspendReason} onChange={(e) => setSuspendReason(e.target.value)} />
          <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
            <button style={secondaryBtnStyle} onClick={() => setShowSuspend(false)} disabled={busy}>Cancel</button>
            <button
              style={dangerBtnStyle}
              disabled={busy}
              onClick={() =>
                runAction(() => suspendCompany(token, id, suspendReason)).then(() => setShowSuspend(false))
              }
            >
              Confirm suspension
            </button>
          </div>
        </Modal>
      )}

      {showCancel && (
        <Modal onClose={() => setShowCancel(false)}>
          <h3 style={{ marginTop: 0 }}>Cancel subscription</h3>
          <p style={{ fontSize: 13, color: "#64748b" }}>
            Same as Suspend — the company's users lose access immediately and its data is not
            deleted. Cancelling (rather than just suspending) is what unlocks the "Delete
            permanently" action below, and can still be undone with Reactivate.
          </p>
          <label style={labelStyle}>Reason (optional)</label>
          <input style={inputStyle} value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} />
          <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
            <button style={secondaryBtnStyle} onClick={() => setShowCancel(false)} disabled={busy}>Cancel</button>
            <button
              style={dangerBtnStyle}
              disabled={busy}
              onClick={() =>
                runAction(() => cancelCompany(token, id, cancelReason)).then(() => setShowCancel(false))
              }
            >
              Confirm cancellation
            </button>
          </div>
        </Modal>
      )}

      {showDelete && (
        <Modal
          onClose={() => {
            // Closing mid-deletion would just hide the progress, not stop
            // it (the goroutine keeps running server-side regardless) — so
            // the overlay/✕ close is only offered before Confirm is
            // clicked, or once it's finished with an error the Super Admin
            // has actually seen.
            if (!deleting || deleteError) {
              setShowDelete(false);
              setDeleting(false);
              setDeleteStage(null);
              setDeleteError(null);
              setDeleteTimedOut(false);
              setDeleteConfirmText("");
            }
          }}
        >
          {!deleting ? (
            <>
              <h3 style={{ marginTop: 0 }}>Delete company permanently</h3>
              <p style={{ fontSize: 13, color: "#64748b" }}>
                This cannot be undone. It drops the company's own database, deletes its uploaded
                files, and removes its record entirely.
              </p>
              <label style={labelStyle}>
                Type <strong>{company.subdomain}</strong> to confirm
              </label>
              <input
                style={inputStyle}
                value={deleteConfirmText}
                onChange={(e) => setDeleteConfirmText(e.target.value)}
                placeholder={company.subdomain}
              />
              <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
                <button style={secondaryBtnStyle} onClick={() => setShowDelete(false)} disabled={busy}>Cancel</button>
                <button
                  style={dangerBtnStyle}
                  disabled={busy || deleteConfirmText.trim() !== company.subdomain}
                  onClick={runDelete}
                >
                  Delete permanently
                </button>
              </div>
            </>
          ) : (
            <>
              <h3 style={{ marginTop: 0 }}>Deleting {company.name}…</h3>
              <div style={progressBoxStyle}>
                {DELETE_STEPS.map((step) => {
                  const state = deletionStepState(step.key, deleteStage, !!deleteError);
                  return (
                    <div key={step.key} style={{ display: "flex", alignItems: "center", gap: 8, padding: "4px 0" }}>
                      <StepIcon state={state} />
                      <span style={{ fontSize: 13, color: state === "waiting" ? "#94a3b8" : "#334155" }}>
                        {step.label}
                      </span>
                    </div>
                  );
                })}
                {deleteError && (
                  <div style={{ fontSize: 12, color: "#991b1b", marginTop: 6 }}>{deleteError}</div>
                )}
                {!deleteError && deleteTimedOut && (
                  <div style={{ fontSize: 12, color: "#92400e", marginTop: 6 }}>
                    Still working — this can occasionally take longer than usual.
                  </div>
                )}
                {!deleteError && !deleteTimedOut && (
                  <div style={{ fontSize: 12, color: "#94a3b8", marginTop: 6 }}>Checking every couple of seconds…</div>
                )}
              </div>
              {deleteError && (
                <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 16 }}>
                  <button
                    style={secondaryBtnStyle}
                    onClick={() => {
                      setShowDelete(false);
                      setDeleting(false);
                      setDeleteStage(null);
                      setDeleteError(null);
                      setDeleteTimedOut(false);
                    }}
                  >
                    Close
                  </button>
                </div>
              )}
            </>
          )}
        </Modal>
      )}

      {showResetPassword && (
        <Modal onClose={() => setShowResetPassword(false)}>
          <h3 style={{ marginTop: 0 }}>Reset admin login</h3>
          <p style={{ fontSize: 13, color: "#64748b" }}>
            Always resets the password. If the login email is also wrong (e.g. it shows
            admin@example.com instead of what you typed when adding the company), fix it here too.
          </p>
          <label style={labelStyle}>New admin email (optional — leave blank to keep the current one)</label>
          <input
            style={inputStyle}
            type="email"
            placeholder={company.contact_email || "admin@yourcompany.com"}
            value={resetEmailInput}
            onChange={(e) => setResetEmailInput(e.target.value)}
          />
          <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
            <button style={secondaryBtnStyle} onClick={() => setShowResetPassword(false)} disabled={busy}>Cancel</button>
            <button style={primaryBtnStyle} disabled={busy} onClick={() => runResetPassword(resetEmailInput.trim())}>
              {resetEmailInput.trim() ? "Update login" : "Reset password"}
            </button>
          </div>
        </Modal>
      )}

      {resetResult && (
        <Modal onClose={() => setResetResult(null)}>
          <h3 style={{ marginTop: 0 }}>Admin password reset</h3>
          <p style={{ fontSize: 13, color: "#64748b" }}>
            Save this password now — it will not be shown again.
          </p>
          <label style={labelStyle}>Admin email</label>
          <input style={inputStyle} readOnly value={resetResult.admin_email} onFocus={(e) => e.target.select()} />
          <label style={labelStyle}>New password</label>
          <input style={inputStyle} readOnly value={resetResult.admin_password} onFocus={(e) => e.target.select()} />
          <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 16 }}>
            <button style={primaryBtnStyle} onClick={() => setResetResult(null)}>Done</button>
          </div>
        </Modal>
      )}
    </div>
  );
}

function InfoCard({ label, value, sub }) {
  return (
    <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 10, padding: 14 }}>
      <div style={{ fontSize: 11, color: "#94a3b8", textTransform: "uppercase", fontWeight: 600 }}>{label}</div>
      <div style={{ fontSize: 15, color: "#0f172a", fontWeight: 600, marginTop: 4 }}>{value}</div>
      {sub && <div style={{ fontSize: 12, color: "#ef4444", marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

function Modal({ children, onClose }) {
  return (
    <div style={overlayStyle} onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div style={modalStyle}>{children}</div>
    </div>
  );
}

const cardGridStyle = { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 12 };
const eventRowStyle = { padding: "12px 16px", borderTop: "1px solid #f1f5f9" };
const backBtnStyle = { background: "none", border: "none", color: "#2563eb", fontSize: 13, cursor: "pointer", marginBottom: 16, padding: 0 };
const inputStyle = { padding: "9px 12px", borderRadius: 8, border: "1px solid #cbd5e1", fontSize: 14, width: "100%", boxSizing: "border-box" };
const labelStyle = { display: "block", fontSize: 12, fontWeight: 600, color: "#475569", margin: "12px 0 4px" };
const primaryBtnStyle = { background: "#0f172a", color: "#fff", border: "none", borderRadius: 8, padding: "9px 14px", fontSize: 13, fontWeight: 600, cursor: "pointer" };
const secondaryBtnStyle = { background: "#fff", color: "#334155", border: "1px solid #cbd5e1", borderRadius: 8, padding: "9px 14px", fontSize: 13, cursor: "pointer" };
const dangerBtnStyle = { background: "#dc2626", color: "#fff", border: "none", borderRadius: 8, padding: "9px 14px", fontSize: 13, fontWeight: 600, cursor: "pointer" };
const overlayStyle = { position: "fixed", inset: 0, background: "rgba(15, 23, 42, 0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 };
const modalStyle = { background: "#fff", borderRadius: 12, padding: 24, width: 380, maxWidth: "90vw" };
const progressBoxStyle = { background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 8, padding: "10px 12px" };
